import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Table,
  Modal,
  Select,
  Popconfirm,
  Space,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import {
  HomeOutlined,
  DeleteOutlined,
  TableOutlined,
  QrcodeOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  getDiningTables,
  createDiningTables,
  updateDiningTables,
  deleteDiningTables,
} from "../../../network/network";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { useTranslation } from "react-i18next";
import { getBranches } from "../../../network/network";
import { AppContext } from "../../../context/AppContextProvider";
import {
  AddonName,
  UserType,
} from "../../../configs/constants";
import { handleApiError } from "../../../utils/errorHandler";

const Tables = () => {
  const { authUser } = useContext(AppContext);
  const ADD_TABLE = "Add Table";
  const ADD_DRIVE_THRU = "Add Drive-through";
  const [loadingBTN, setLoadingBTN] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [reloadTableData, setReloadTableData] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [branchesData, setBranchesData] = useState([]);
  const [tablesData, setTablesData] = useState([]);
  const [modalTitle, setModalTitle] = useState(ADD_TABLE);
  const [searchParams, setSearchParams] = useState([]);
  const [tableForm] = Form.useForm();
  const [searchFormRef] = Form.useForm();
  const { t } = useTranslation();

  const is_QR_addon_active = authUser.active_addons.some(
    (addon) => addon.id && addon.name === AddonName.QrOrdering,
  );

  useEffect(() => {
    getBranches({ per_page: -1 })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setBranchesData(response.data.data.branches);
      })
      .catch((error) => handleApiError(error, t));
  }, [t]);

  useEffect(() => {
    setIsDataLoading(true);
    getDiningTables(searchParams)
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setTablesData(response.data.data.dining_tables);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [reloadTableData, searchParams, t]);

  const showTableModal = () => {
    // Show Modal
    setModalVisible(true);
    // Set Modal Title
    setModalTitle(t("add_table"));
    // Explicitly reset Form Fields before setting them for edit
    // This ensures the form is in a clean state before being repopulated
    tableForm.resetFields();

    // Set Form Fields
    tableForm.setFieldsValue({
      is_drive_thru: 0,
    });
  };

  const showDriveThruModal = () => {
    // Show Modal
    setModalVisible(true);
    // Set Modal Title
    setModalTitle(t("drive_thru_add"));
    // Explicitly reset Form Fields before setting them for edit
    // This ensures the form is in a clean state before being repopulated
    tableForm.resetFields();

    // Set Form Fields
    tableForm.setFieldsValue({
      is_drive_thru: 1,
    });
  };

  const handleAPIResponse = (response, type) => {
    let data = response.data || response.response.data;
    if (data.success === true) {
      setReloadTableData(!reloadTableData);
      // Success
      if (type === ADD_TABLE) {
        NotificationWithIcon(
          "success",
          t("table_added"),
          t("table_added_successfully"),
        );
      } else if (type === ADD_DRIVE_THRU) {
        NotificationWithIcon(
          "success",
          t("drive_thru_added"),
          t("drive_thru_added_successfully"),
        );
      } else {
        NotificationWithIcon(
          "success",
          t("entity_updated"),
          t("entity_updated_successfully"),
        );
      }
    } else {
      handleApiError(response, t);
    }
    // Reset Form Fields
    tableForm.resetFields();
    // Hide Modal
    setModalVisible(false);
  };

  const handleAddEdit = () => {
    tableForm
      .validateFields()
      .then((values) => {
        // Set Loading True
        setLoadingBTN(true);
        // Sending Request to API
        if (modalTitle === ADD_TABLE || modalTitle === t("add_table")) {
          createDiningTables(values)
            .then((response) => handleAPIResponse(response, ADD_TABLE))
            .catch((error) => handleApiError(error, t))
            .finally(() => {
              // Set Loading False
              setLoadingBTN(false);
            });
        } else if (
          modalTitle === ADD_DRIVE_THRU ||
          modalTitle === t("drive_thru_add")
        ) {
          createDiningTables(values)
            .then((response) => handleAPIResponse(response, ADD_DRIVE_THRU))
            .catch((error) => handleApiError(error, t))
            .finally(() => {
              // Set Loading False
              setLoadingBTN(false);
            });
        } else {
          updateDiningTables(values)
            .then((response) => handleAPIResponse(response, null))
            .catch((error) => handleApiError(error, t))
            .finally(() => {
              // Set Loading False
              setLoadingBTN(false);
            });
        }
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const handleEditModal = (id) => {
    // Find branch from DataArray
    const table = tablesData.find((table) => table.id === id);

    // Ensure values are not undefined
    const name = table.name || ""; // Default to empty string if undefined
    const branchId = table.branch?.id || ""; // Default to empty string if undefined

    // Set Modal Title
    setModalTitle(t("edit") + " : " + name);

    // Show Modal
    setModalVisible(true);

    // Explicitly reset Form Fields before setting them for edit
    // This ensures the form is in a clean state before being repopulated
    tableForm.resetFields();

    // Set Form Fields
    tableForm.setFieldsValue({
      id: table.id,
      name: name,
      branch_id: branchId,
      is_drive_thru: table.is_drive_thru,
    });
  };

  const handleCancel = () => {
    // Reset Form Fields
    tableForm.resetFields();
    setModalVisible(false);
  };

  const handleDelete = (id) => {
    deleteDiningTables(id)
      .then((response) => {
        if (response.data.success === true) {
          setReloadTableData(!reloadTableData);
          // Success
          NotificationWithIcon(
            "success",
            t("entity_deleted"),
            t("entity_deleted_successfully"),
          );
        } else {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  };

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "tables",
      breadcrumbName: t("tables"),
      icon: <TableOutlined />,
    },
  ];

  const ColumnsData = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => {
        return <>{index + 1}</>;
      },
    },
    {
      title: t("name"),
      render: (record) => {
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            {record.name}
            {record.is_drive_thru ? (
              <img
                src="/icons/car.svg"
                alt={t("qr")}
                style={{
                  width: "30px",
                  height: "auto",
                  marginLeft: "10px",
                }}
              />
            ) : (
              <img
                src="/icons/dining-table.svg"
                alt={t("qr")}
                style={{
                  width: "30px",
                  height: "auto",
                  marginLeft: "10px",
                }}
              />
            )}
          </div>
        );
      },
    },
    {
      title: t("branch"),
      dataIndex: "branch",
      render: (branch) => {
        return branch.name;
      },
    },
    {
      title: t("action"),
      align: "center",
      width: 150,
      render: (record) => {
        return (
          <Space>
            {is_QR_addon_active && record.qr_code_url && (
              <Button
                type="link"
                title={t("qr")}
                href={record.qr_code_url}
                size="small"
                target="_blank"
              >
                <QrcodeOutlined />
              </Button>
            )}
            {authUser.type !== UserType.Employee && (
              <Button
                type="link"
                onClick={() => handleEditModal(record.id)}
                title={t("edit")}
              >
                <EditOutlined />
              </Button>
            )}
            {authUser.type !== UserType.Employee && (
              <Popconfirm
                title={t("warning_delete")}
                onConfirm={() => handleDelete(record.id)}
                okText={t("yes")}
                cancelText={t("no")}
                placement="topLeft"
              >
                <Button type="link" title={t("delete_entity")}>
                  <DeleteOutlined />
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  const handleSearch = (values) => {
    let search_params = false;
    if (values.filter_by_branch_id) {
      search_params = {
        branch_id: values.filter_by_branch_id,
      };
    }

    if (search_params) {
      setSearchParams(search_params);
    } else {
      setSearchParams([]);
    }
  };

  const handleReset = () => {
    // Reset Form Fields
    searchFormRef.resetFields();
    // Reset Transactions List
    setSearchParams([]);
  };

  const searchForm = (
    <>
      <Form
        name="search-form"
        layout="inline"
        onFinish={handleSearch}
        form={searchFormRef}
      >
        <Form.Item name="filter_by_branch_id" label={t("branch")}>
          <Select
            className="app-w-200px"
            options={branchesData.map((branch) => {
              return {
                label: branch.name,
                value: branch.id,
              };
            })}
          ></Select>
        </Form.Item>
        <Form.Item>
          <Input
            type="submit"
            value={t("search")}
            className="app-search-submit"
          />
        </Form.Item>
        <Form.Item>
          <Input type="button" value={t("reset")} onClick={handleReset} />
        </Form.Item>
      </Form>
    </>
  );

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("tables")}
          ghost={false}
          footer={authUser.type !== UserType.Employee && searchForm}
          extra={[
            authUser.type !== UserType.Employee && (
              <>
                <Button
                  key="open-add-DriveThru-modal"
                  type="primary"
                  onClick={showDriveThruModal}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <img
                    src="/icons/car-white.svg"
                    alt={t("qr")}
                    className="app-order-meta-icon"
                  />
                  {t("drive_thru_add")}
                </Button>
                <Button
                  key="open-add-store-modal"
                  type="primary"
                  onClick={showTableModal}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <img
                    src="/icons/dining-table-white.svg"
                    alt={t("qr")}
                    className="app-order-meta-icon"
                  />
                  {t("add_table")}
                </Button>
              </>
            ),
          ]}
        />
      </Card>
      <Card>
        <Table
          columns={ColumnsData}
          dataSource={tablesData}
          loading={isDataLoading}
          rowKey="id"
          pagination={false}
        />
      </Card>
      <Modal
        open={modalVisible}
        title={modalTitle}
        onOk={handleAddEdit}
        onCancel={handleCancel}
        footer={[
          <Button
            type="default"
            key="back"
            loading={loadingBTN}
            onClick={handleCancel}
          >
            {t("cancel")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loadingBTN}
            onClick={handleAddEdit}
          >
            {modalTitle === ADD_TABLE || modalTitle === t("add_table")
              ? t("add_table")
              : modalTitle === ADD_DRIVE_THRU ||
                  modalTitle === t("drive_thru_add")
                ? t("drive_thru_add")
                : t("update")}
          </Button>,
        ]}
      >
        <Form
          name="add-table-form"
          layout="vertical"
          onFinish={handleAddEdit}
          className="app-form-full"
          form={tableForm}
        >
          <Form.Item
            name="name"
            label={t("name")}
            rules={[
              {
                required: true,
                message: t("validate_name_required"),
                whitespace: true,
              },
            ]}
            hasFeedback
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="branch_id"
            label={t("branch")}
            rules={[
              {
                required: true,
                message: t("validate_branch_required"),
              },
            ]}
            hasFeedback
          >
            <Select
              showSearch
              allowClear
              options={branchesData.map((branch) => {
                return {
                  label: branch.name,
                  value: branch.id,
                };
              })}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item name="is_drive_thru" className="app-hidden">
            <input type="hidden" />
          </Form.Item>
          <Form.Item name="id" className="app-hidden">
            <input type="hidden" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Tables;
